import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import { injectIntl } from 'react-intl';
import styles from './styles';

const StyledCard = styled(Card)(({ selected }) => styles.sleeperCard(selected));
const StyledCardContent = styled(CardContent)(styles.sleeperCardContent);
const PriceTypography = styled(Typography)(styles.priceTypography);
const DescriptionTypography = styled(Typography)(styles.descriptionTypography);
const AvailabilityContainer = styled('div')(styles.availabilityContainer);
const AvailabilityChip = styled(Chip)(({ availability }) =>
	styles.availabilityChip(availability),
);

const RenderSleeperSupplements = ({
	sleeperSupplements,
	onCheckSleeperSupplement,
	selectedSleeperSupplements,
}) => {
	const handleSleeperSupplementChange = (event) => {
		const selectedSupplementId = event.target.value;
		const selectedSupplement = sleeperSupplements.find(
			(supplement) => supplement.id === selectedSupplementId,
		);

		if (selectedSupplement) {
			onCheckSleeperSupplement(
				selectedSupplement,
				true,
				selectedSupplement.linkedTravelSegments[0],
				selectedSupplement.description,
			);
		}
	};

	const supplementsMatch = (supplement, selected) => {
		const supplementOriginalId = supplement.originalId || supplement.id;
		const selectedOriginalId = selected.originalId || selected.id;
		const supplementSegment = supplement.linkedTravelSegments[0];
		const selectedSegment = selected.linkedTravelSegments?.[0];

		return (
			supplementOriginalId === selectedOriginalId &&
			supplementSegment === selectedSegment
		);
	};

	const getSelectedSupplementId = () => {
		const selectedSupplement = sleeperSupplements.find((supplement) =>
			selectedSleeperSupplements.some((selected) =>
				supplementsMatch(supplement, selected),
			),
		);
		return selectedSupplement ? selectedSupplement.id : '';
	};

	const isSupplementSelected = (supplement) =>
		selectedSleeperSupplements.some((selected) =>
			supplementsMatch(supplement, selected),
		);

	const getAvailabilityText = (supplement) => {
		const availability = supplement.supplementAvailabilities[0];
		if (!availability || availability.quantity === 0) return null;
		if (availability.quantity > 9) return '9+ available';
		return `${availability.quantity} available`;
	};

	const sortedSupplements = sleeperSupplements.sort(
		(a, b) => a.value - b.value,
	);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<RadioGroup
					value={getSelectedSupplementId()}
					onChange={handleSleeperSupplementChange}
				>
					<Grid container spacing={2}>
						{sortedSupplements.map((supplement) => {
							const availability = supplement.supplementAvailabilities[0];
							const availabilityQuantity = availability
								? availability.quantity
								: 10;
							const availabilityText = getAvailabilityText(supplement);
							const availabilityColor =
								availability && availability.quantity <= 3
									? 'warning'
									: 'success';

							return (
								<Grid item xs={12} sm={6} key={supplement.id}>
									<StyledCard selected={isSupplementSelected(supplement)}>
										<StyledCardContent>
											<FormControlLabel
												value={supplement.id}
												control={<Radio color="primary" />}
												label={
													<div style={{ width: '100%' }}>
														<PriceTypography variant="h6">
															{supplement.priceDesc}
														</PriceTypography>
														<DescriptionTypography variant="body2">
															{supplement.description}
														</DescriptionTypography>
														{availabilityText && (
															<AvailabilityContainer>
																<AvailabilityChip
																	label={availabilityText}
																	size="small"
																	color={availabilityColor}
																	availability={availabilityQuantity}
																/>
															</AvailabilityContainer>
														)}
													</div>
												}
												sx={{
													margin: 0,
													width: '100%',
													'& .MuiFormControlLabel-label': {
														width: '100%',
													},
												}}
											/>
										</StyledCardContent>
									</StyledCard>
								</Grid>
							);
						})}
					</Grid>
				</RadioGroup>
			</Grid>
		</Grid>
	);
};

RenderSleeperSupplements.propTypes = {
	sleeperSupplements: PropTypes.array.isRequired,
	onCheckSleeperSupplement: PropTypes.func.isRequired,
	selectedSleeperSupplements: PropTypes.array.isRequired,
};

export { RenderSleeperSupplements as RenderSleeperSupplementsAlias };

export default injectIntl(RenderSleeperSupplements);
